/**
 * Bozza di ottimizzazione (SPJ-1366): una configurazione iniziata e mai lanciata.
 *
 * Non serve salvarla a parte: persona tiene nello stato del thread `pending-config-id` (il
 * config di lavoro) e `optim-dataset` finche' l'ottimizzazione non viene lanciata, e li
 * ripristina se la run fallisce. Qui si legge solo quello stato.
 */

type AppState = Record<string, unknown> | undefined | null;

export type OptimizationDraft = {
  configId: number;
  datasetId: number;
};

function toPositiveInt(value: unknown): number | null {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}

function historyEntries(appState: AppState): Record<string, unknown>[] {
  const history = appState?.['history'];
  if (!Array.isArray(history)) return [];
  return history.filter(
    (entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object' && !Array.isArray(entry),
  );
}

/** Il progetto ha almeno una run riuscita. */
export function hasSucceededRun(appState: AppState): boolean {
  return historyEntries(appState).some((entry) => entry.status === 'succeeded');
}

/**
 * La bozza del progetto, se c'e': un config di lavoro e un dataset. Non e' una bozza se il
 * config di lavoro e' quello di una run riuscita (in quel caso e' il config della run).
 */
export function getOptimizationDraft(appState: AppState): OptimizationDraft | null {
  const configId = toPositiveInt(appState?.['pending-config-id']);
  const dataset = appState?.['optim-dataset'];
  const datasetId = dataset && typeof dataset === 'object' ? toPositiveInt((dataset as Record<string, unknown>).id) : null;
  if (configId === null || datasetId === null) return null;

  const isRunConfig = historyEntries(appState).some(
    (entry) => entry.status === 'succeeded' && toPositiveInt(entry.config_id) === configId,
  );
  return isRunConfig ? null : { configId, datasetId };
}
